import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, ExternalLink, Eye, FileCheck2, FileDown, Link2Off, MessageSquareText, Send } from 'lucide-react'
import { StatusPill } from '@/components/status-pill'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import type { StatusTone } from '@/lib/status-tones'
import { useQuoteBuilderApi } from '../api/client'
import { ErrorState, LoadingState, PageHeader, PageState, SurfaceCard } from '../ui/page'
import {
  CUSTOMER_ACTIVITY,
  documentUrl,
  publicationsQueryOptions,
  publishingApi,
  quoteActivityQueryOptions,
  type PublicationListItem,
  type QuoteActivity,
} from './api'
import { FollowUpCard } from './follow-up-card'
import { SignatureDetails } from './signature-details'

const dateTime = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' })
const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const decisionLabels: Record<PublicationListItem['decision_state'], [string, StatusTone]> = {
  pending: ['Awaiting customer', 'neutral'],
  approved: ['Approved', 'success'],
  rejected: ['Declined', 'danger'],
  changes_requested: ['Changes requested', 'warning'],
}

function activityTitle(item: QuoteActivity) {
  const payload = item.payload as Record<string, unknown>
  switch (item.activity_type) {
    case 'quote_sent':
      return 'Quote sent'
    case 'invoice_sent':
      return 'Invoice sent'
    case 'share_revoked':
      return 'Customer link turned off'
    case 'quote_published':
      return `${payload.mode === 'final' ? 'Final quote' : 'Draft'} published`
    case 'customer_comment_added':
      return 'Customer comment added'
    case 'customer_opened':
      return `Customer opened the ${payload.mode === 'final' ? 'final quote' : 'draft'}`
    default:
      return payload.decision === 'approved'
        ? payload.signed
          ? 'Quote signed and approved'
          : 'Quote approved'
        : payload.decision === 'rejected'
          ? 'Quote declined'
          : 'Changes requested'
  }
}

function activityDetail(item: QuoteActivity) {
  const payload = item.payload as Record<string, unknown>
  if ((item.activity_type === 'quote_sent' || item.activity_type === 'invoice_sent') && typeof payload.recipient_email === 'string') {
    return `To ${payload.recipient_email}`
  }
  if (item.activity_type === 'customer_decision_recorded' && typeof payload.name === 'string') return `By ${payload.name}`
  if (item.activity_type === 'customer_comment_added' && typeof payload.author_name === 'string') return `From ${payload.author_name}`
  if (typeof payload.publication_number === 'number') return `Publication ${payload.publication_number}`
  return null
}

/** Published revisions, the customer link, comments and decisions (demo `ShareActivityView`). */
export function CustomerSharePage({ quoteId }: { quoteId: string }) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const publications = useQuery(publicationsQueryOptions(api, quoteId))
  const activity = useQuery(quoteActivityQueryOptions(api, quoteId))
  const revoke = useMutation({
    mutationFn: () => publishingApi.revoke(api, quoteId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['quote-publications', quoteId] })
      void queryClient.invalidateQueries({ queryKey: ['quote-activity', quoteId] })
    },
  })

  if (publications.isLoading || activity.isLoading) {
    return <LoadingState label="Loading customer activity" />
  }
  if (!publications.data || !activity.data) {
    return (
      <ErrorState
        description="Reload the page to try again."
        onRetry={() => {
          void publications.refetch()
          void activity.refetch()
        }}
        title="Customer activity unavailable"
      />
    )
  }
  const customerEvents = activity.data.filter((item) =>
    (CUSTOMER_ACTIVITY as readonly string[]).includes(item.activity_type),
  )
  const activeLink = publications.data.some((item) => item.link_state === 'active')

  return (
    <div className="mx-auto w-full max-w-5xl">
      <PageHeader
        actions={
          <Button asChild size="sm" variant="outline">
            <Link params={{ quoteId, stage: 'equipment' }} to="/quotes/$quoteId/workflow/$stage">
              <ArrowLeft aria-hidden="true" />
              Open quote
            </Link>
          </Button>
        }
        description="Published revisions, comments and customer decisions"
        title="Customer share"
      />
      <SurfaceCard
        actions={
          activeLink ? (
            <LoadingButton
              loading={revoke.isPending}
              loadingText="Turning off..."
              onClick={() => revoke.mutate()}
              size="sm"
              variant="outline"
            >
              <Link2Off aria-hidden="true" />
              Turn off link
            </LoadingButton>
          ) : undefined
        }
        className="mb-4"
        title="Publications"
      >
        {publications.data.length ? (
          <ul className="divide-y">
            {publications.data.map((item, index) => {
              const [decision, tone] = decisionLabels[item.decision_state]
              return (
                <li
                  className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                  key={item.publication_number}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">
                        {item.mode === 'final' ? 'Final quote' : 'Draft'} · Publication {item.publication_number}
                      </span>
                      {item.current ? <StatusPill tone="progress">Current</StatusPill> : null}
                      {item.mode === 'final' ? <StatusPill tone={tone}>{decision}</StatusPill> : null}
                      {item.link_state !== 'active' ? (
                        <StatusPill tone="neutral">{item.link_state === 'revoked' ? 'Link off' : 'Link expired'}</StatusPill>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Revision {item.source_revision} · {dateTime.format(new Date(item.published_at))}
                      {index === 0 && item.link_state === 'active'
                        ? ` · Link active until ${shortDate.format(new Date(item.expires_at))}`
                        : ''}
                    </p>
                    {item.signature ? (
                      <div className="mt-3 rounded-lg border bg-muted/40 p-3">
                        <SignatureDetails signature={item.signature} />
                      </div>
                    ) : null}
                  </div>
                  {/* The link always opens the latest publication. */}
                  {index === 0 && item.link_state === 'active' ? (
                    <div className="flex flex-wrap gap-2">
                      <Button asChild size="sm" variant="outline">
                        {/* eslint-disable-next-line no-restricted-syntax -- PDF download from the API */}
                        <a href={documentUrl(item.document_url)}>
                          <FileDown aria-hidden="true" />
                          PDF
                        </a>
                      </Button>
                      <Button asChild size="sm" variant="outline">
                        {/* eslint-disable-next-line no-restricted-syntax -- the customer portal is another app */}
                        <a href={item.share_url} rel="noreferrer" target="_blank">
                          <ExternalLink aria-hidden="true" />
                          Customer view
                        </a>
                      </Button>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <PageState compact kind="empty" title="Not published yet" />
        )}
        {revoke.error ? (
          <p className="border-t px-4 py-2 text-sm text-destructive" role="alert">
            The link could not be turned off. Try again.
          </p>
        ) : null}
      </SurfaceCard>
      <FollowUpCard quoteId={quoteId} />
      <SurfaceCard title="Activity">
        {customerEvents.length ? (
          <ul className="divide-y">
            {customerEvents.map((item) => {
              const Icon =
                item.activity_type === 'quote_published'
                  ? FileCheck2
                  : item.activity_type === 'customer_comment_added'
                    ? MessageSquareText
                    : item.activity_type === 'quote_sent' || item.activity_type === 'invoice_sent'
                      ? Send
                      : item.activity_type === 'share_revoked'
                        ? Link2Off
                        : item.activity_type === 'customer_opened'
                          ? Eye
                          : CheckCircle2
              const detail = activityDetail(item)
              return (
                <li className="flex gap-3 p-4" key={item.id}>
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-foreground">
                    <Icon aria-hidden="true" className="size-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{activityTitle(item)}</p>
                    {detail ? <p className="mt-0.5 break-all text-xs text-muted-foreground">{detail}</p> : null}
                    <p className="mt-0.5 text-xs text-muted-foreground">{dateTime.format(new Date(item.created_at))}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <PageState compact kind="empty" title="No customer activity yet" />
        )}
      </SurfaceCard>
    </div>
  )
}
